import { useEffect, useRef, useState } from "react";
import type { EChartsOption } from "echarts";
import { echarts, ATLAS_CHART_THEME_NAME } from "../lib/echarts.js";
import { canonicalizeFilterValue } from "../lib/pageFilters.js";

// Callers should memoize `option` to avoid redundant setOption calls.
// For static data, define the option object outside the component.
//
// Post-ADR-0080: Chart is a thin renderer. Every option arrives fully resolved
// — `tooltip.formatter`, `axisLabel.formatter`, etc. are real functions
// produced by the relevant per-chart-type builder in `lib/echartsRenderer/`.
// The legacy `atlasTooltip` / `atlasValueFormat` declarative-escape namespace
// and the option-tree walker that resolved them are gone with the
// `output.visualization` retirement.

type ChartPointClick = {
  value: string;
  column?: string;
  seriesName?: string;
};

function resolveClickedColumn(record: Record<string, unknown>): string | undefined {
  const encode = typeof record.encode === "object" && record.encode !== null
    ? (record.encode as Record<string, unknown>)
    : null;
  const xEncode = encode?.x;
  if (typeof xEncode === "string") return xEncode;

  const dimensionNames = Array.isArray(record.dimensionNames)
    ? (record.dimensionNames as unknown[]).filter((name): name is string => typeof name === "string")
    : [];

  const encodedIndices = Array.isArray(xEncode)
    ? xEncode
    : (typeof xEncode === "number" || typeof xEncode === "string")
      ? [xEncode]
      : [];
  for (const idx of encodedIndices) {
    if (typeof idx === "string") return idx;
    if (typeof idx === "number" && idx >= 0 && idx < dimensionNames.length) {
      return dimensionNames[idx];
    }
  }

  if (dimensionNames.length > 0) return dimensionNames[0];
  return undefined;
}

function resolveClickedValue(record: Record<string, unknown>): unknown {
  // Dataset-driven series (ADR-0079 §4.4): `params.value` is the raw row array
  // and the x-encoded dimension is the source of truth. Prefer it over
  // `params.name` — for `time` xAxis types echarts surfaces `name` as the
  // axis-label-formatted display string (e.g. "Jan 15"), which won't match
  // the raw value held by a downstream table cell.
  const raw = record.value;
  if (Array.isArray(raw)) {
    const encode = typeof record.encode === "object" && record.encode !== null
      ? (record.encode as Record<string, unknown>)
      : null;
    const xEncode = encode?.x;
    const xIndices = Array.isArray(xEncode)
      ? xEncode
      : (typeof xEncode === "number" ? [xEncode] : []);
    for (const idx of xIndices) {
      if (typeof idx === "number" && idx >= 0 && idx < raw.length) {
        return raw[idx];
      }
    }
    return raw[0];
  }
  if (record.name != null) return record.name;
  return raw;
}

export function Chart({
  option,
  height = 280,
  onPointClick
}: {
  option: EChartsOption;
  height?: number | string;
  onPointClick?: (point: ChartPointClick) => void;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof echarts.init> | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  // Init + update
  useEffect(() => {
    if (!elRef.current) return;
    if (!chartRef.current) {
      chartRef.current = echarts.init(elRef.current, ATLAS_CHART_THEME_NAME);
    }
    try {
      chartRef.current.setOption(option, { notMerge: true });
      setRenderError(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error("[Chart] ECharts setOption failed:", message);
      setRenderError(message);
    }
  }, [option]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !onPointClick) return;
    const handler = (params: unknown) => {
      if (!params || typeof params !== "object") return;
      const record = params as Record<string, unknown>;
      const rawValue = resolveClickedValue(record);
      if (rawValue == null) return;
      const column = resolveClickedColumn(record);
      const canonical = canonicalizeFilterValue(rawValue);
      if (canonical === "") return;
      onPointClick({
        value: canonical,
        ...(column ? { column } : {}),
        ...(typeof record.seriesName === "string" ? { seriesName: record.seriesName } : {})
      });
    };
    chart.on("click", handler as (params: unknown) => void);
    return () => {
      chart.off("click", handler as (params: unknown) => void);
    };
  }, [onPointClick]);

  // Resize on container size change
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      chartRef.current?.resize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  return (
    <>
      <div ref={elRef} style={{ width: "100%", height, display: renderError ? "none" : undefined }} />
      {renderError ? (
        <div style={{ padding: 16, color: "var(--color-error, #c00)", fontSize: 13 }}>
          <strong>Chart render error:</strong> {renderError}
          <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
            Click <strong>Full Refresh</strong> to regenerate the chart.
          </div>
        </div>
      ) : null}
    </>
  );
}
